import React, { useState, useEffect } from 'react';
import './DeclineJobConfirmModal.css';

// Confirmation dialog for a Company SP declining a job outright — no
// inspector routing at all. Called from JobsPage.js's Requests tab when the
// dispatcher hits the red "Decline Request" button. Companion to AssignJobModal.js
// (which handles the Assign / Re-Assign flow); this one is intentionally
// smaller: just a Yes/No confirm with an optional reason surfaced to the
// requester in their SR_COMPANY_DECLINED notification.
//
// Structural mirror of AssignJobModal — same overlay + drag-handle + header
// + footer chrome — so the two dialogs feel identical in weight and
// motion. Diverges only in the body (no vendor list, just a textarea).
//
// Owns local submitting/error state; parent (JobsPage) owns the network
// call, passed in as onConfirm(reason) that returns a promise. Modal
// disables its own controls + backdrop while the promise is pending.
const DeclineJobConfirmModal = ({ visible, job, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset on open — a leftover reason from a previous open would be
  // surprising (dispatcher might not notice a stale textarea and blast a
  // wrong reason to the requester).
  useEffect(() => {
    if (!visible) return;
    setReason('');
    setSubmitting(false);
    setError('');
  }, [visible, job]);

  if (!visible || !job) return null;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(reason.trim() || undefined);
    } catch (err) {
      setError(
        err?.response?.data?.message
          || 'Failed to decline the request. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="djcm-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="djcm-container">
        <div className="djcm-drag-handle" />
        <div className="djcm-header">
          <span className="djcm-title">Decline this request?</span>
          <button
            className="djcm-close-btn"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <p className="djcm-subtitle">
          The requester will be notified that your company can&apos;t fulfill{' '}
          <strong>{job.address}</strong>. This can&apos;t be undone from here.
        </p>

        <div className="djcm-body">
          <label className="djcm-reason-label" htmlFor="djcm-reason-input">
            Reason (optional)
          </label>
          <textarea
            id="djcm-reason-input"
            className="djcm-reason-input"
            rows={3}
            placeholder="Share why so the requester can adjust and find another provider."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            maxLength={500}
          />
          {error && <div className="djcm-error-banner">{error}</div>}
        </div>

        <div className="djcm-footer">
          <button
            className="djcm-cancel-btn"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="djcm-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                {/* Reuses @keyframes jp-spin from JobsPage.css so we don't
                    fork the animation. 14px stroke matches the button copy
                    height so the spinner sits on the baseline. */}
                <svg
                  className="djcm-spin"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Declining…
              </>
            ) : (
              'Decline Request'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeclineJobConfirmModal;
